/**
 * The picture guide: /help, and /help/<task>.
 *
 * For a cashier who would rather not read. The home screen is a set of big
 * picture tiles, one job each. A job opens one step at a time: a picture of
 * what to press, a sentence of a few words, a button that reads it out, and
 * two big buttons to move on or back. Print turns a job into a poster for the
 * wall next to the till.
 *
 * Public and static, like the shop guide: it works on an unpaired till and
 * with no signal, which is when people need it most.
 */

import { ArrowLeft, ArrowRight, Check, Home, Printer, Volume2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Logo } from "@/components/ui";
import { cn } from "@/lib/cn";

import { TONES } from "./scene";
import { GROUPS, findGroup, findTask, type Group } from "./groups";
import { Picture, say, type Lang, type Task } from "./tasks";

const LANG: Lang = "en";

export function EasyGuidePage() {
  const { task: id } = useParams();
  const found = findTask(id);
  return found ? (
    <TaskView key={found.task.id} task={found.task} group={found.group} />
  ) : (
    <HomeView />
  );
}

// ── Home: what do you want to do? ──────────────────────────────────────────

function HomeView() {
  const [params] = useSearchParams();
  const group = findGroup(params.get("for"));
  return (
    <div className="min-h-screen bg-canvas">
      <header className="bg-navy-900 text-white">
        <div className="mx-auto flex max-w-[1000px] flex-wrap items-center justify-between gap-3 px-4 py-5 sm:px-6">
          <span className="inline-flex rounded-[14px] bg-white px-3 py-2">
            <Logo height={36} label="Jalusi SmartSpaza" />
          </span>
          <Link
            to="/login"
            className="inline-flex h-touch items-center gap-2 rounded-control px-3 font-medium text-brand-100 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
          >
            <ArrowLeft className="size-5" aria-hidden />
            Till
          </Link>
        </div>
        <div className="mx-auto max-w-[1000px] px-4 pb-8 sm:px-6">
          <h1 className="text-[clamp(2rem,6vw,3rem)] font-extrabold leading-tight">
            What do you want to do?
          </h1>
          <p className="mt-1 text-lg text-brand-100">First tap who you are. Then tap a picture.</p>

          {/* ── Who are you? One set of jobs each. ─────────────────────── */}
          <nav
            aria-label="Who are you?"
            className="mt-6 grid grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))] gap-2 sm:gap-3"
          >
            {GROUPS.map((g) => {
              const active = g.id === group.id;
              const tone = TONES[g.tone];
              return (
                <Link
                  key={g.id}
                  to={g.id === GROUPS[0]!.id ? "/help" : `/help?for=${g.id}`}
                  replace
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex items-center gap-3 rounded-2xl border-2 px-3 py-3 transition focus:outline-none focus-visible:ring-4 focus-visible:ring-gold-500",
                    active
                      ? "border-gold-500 bg-white text-ink-900 shadow-raised"
                      : "border-white/15 bg-white/5 text-white hover:bg-white/10",
                  )}
                >
                  <span
                    className={cn(
                      "grid size-11 shrink-0 place-items-center rounded-full",
                      active ? tone.soft : "bg-white/10",
                    )}
                  >
                    <g.icon
                      className={cn("size-6", active ? tone.text : "text-white")}
                      aria-hidden
                    />
                  </span>
                  <span className="grid min-w-0 text-left">
                    <span className="text-lg font-bold leading-tight">{say(g.title, LANG)}</span>
                    <span
                      className={cn("truncate text-xs", active ? "text-ink-500" : "text-brand-100")}
                    >
                      {say(g.who, LANG)}
                    </span>
                  </span>
                </Link>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-[1000px] px-4 py-6 sm:px-6">
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-3 sm:gap-4">
          {group.tasks.map((task) => {
            const tone = TONES[task.tone];
            return (
              <li key={task.id}>
                <Link
                  to={`/help/${task.id}`}
                  className="grid h-full justify-items-center gap-3 rounded-2xl border-2 border-line bg-surface px-3 py-5 text-center shadow-card transition hover:-translate-y-0.5 hover:border-brand-500 hover:shadow-raised focus:outline-none focus-visible:ring-4 focus-visible:ring-gold-500 active:translate-y-0"
                >
                  <span className={cn("grid size-20 place-items-center rounded-full", tone.soft)}>
                    <task.icon className={cn("size-10", tone.text)} aria-hidden />
                  </span>
                  <span className="text-lg font-bold leading-tight text-ink-900">
                    {say(task.title, LANG)}
                  </span>
                  <span className="flex gap-1" aria-label={`${task.steps.length} steps`}>
                    {task.steps.map((_, i) => (
                      <span key={i} className={cn("size-2 rounded-full", tone.solid)} />
                    ))}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>

        <p className="mt-8 text-center text-sm text-ink-500">
          Want to read more?{" "}
          <Link to="/guide" className="font-medium text-brand-500 hover:underline">
            Full guides and training manuals
          </Link>
        </p>
      </main>
    </div>
  );
}

// ── One job, one step at a time ────────────────────────────────────────────

function TaskView({ task, group }: { task: Task; group: Group }) {
  const home = group.id === GROUPS[0]!.id ? "/help" : `/help?for=${group.id}`;
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const done = index >= task.steps.length;
  const step = task.steps[Math.min(index, task.steps.length - 1)]!;
  const tone = TONES[task.tone];
  const heading = useRef<HTMLHeadingElement>(null);

  const next = useCallback(
    () => setIndex((i) => Math.min(i + 1, task.steps.length)),
    [task.steps.length],
  );
  const back = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);

  // Arrow keys on a till with a keyboard; a swipe on a tablet.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, back]);
  const touchX = useRef<number | null>(null);

  // A new step is announced and read from the top.
  useEffect(() => {
    heading.current?.focus();
    stopSpeaking();
  }, [index]);
  useEffect(() => stopSpeaking, []);

  return (
    <div data-print="page" className="flex min-h-screen flex-col bg-canvas print:bg-white">
      <header className="bg-navy-900 text-white print:hidden">
        <div className="mx-auto flex max-w-[980px] items-center gap-3 px-4 py-3 sm:px-6">
          <Link
            to={home}
            className="inline-flex h-touch-lg items-center gap-2 rounded-control bg-white/10 px-3 font-semibold hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
          >
            <Home className="size-5" aria-hidden />
            <span className="sr-only sm:not-sr-only">All jobs</span>
          </Link>
          <span className={cn("grid size-10 shrink-0 place-items-center rounded-full", tone.soft)}>
            <task.icon className={cn("size-5", tone.text)} aria-hidden />
          </span>
          <p className="min-w-0 flex-1 truncate text-xl font-bold">{say(task.title, LANG)}</p>
          <button
            type="button"
            onClick={() => window.print()}
            className="hidden h-touch items-center gap-2 rounded-control px-3 text-sm font-medium text-brand-100 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 sm:inline-flex"
          >
            <Printer className="size-4" aria-hidden />
            Print poster
          </button>
        </div>
      </header>

      {/* ── On screen: one step ─────────────────────────────────────────── */}
      <main
        className="mx-auto flex w-full max-w-[980px] flex-1 flex-col px-4 py-5 sm:px-6 print:hidden"
        onTouchStart={(e) => (touchX.current = e.touches[0]?.clientX ?? null)}
        onTouchEnd={(e) => {
          const start = touchX.current;
          const end = e.changedTouches[0]?.clientX;
          if (start == null || end == null) return;
          if (start - end > 60) next();
          if (end - start > 60) back();
        }}
      >
        <Progress total={task.steps.length} at={index} solid={tone.solid} />

        {done ? (
          <section className="grid flex-1 content-center justify-items-center gap-5 py-8 text-center">
            <span className="grid size-28 place-items-center rounded-full bg-money-500 text-white shadow-raised">
              <Check className="size-16" strokeWidth={3} aria-hidden />
            </span>
            <h1
              ref={heading}
              tabIndex={-1}
              className="text-3xl font-extrabold text-ink-900 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              Well done!
            </h1>
            <div className="flex flex-wrap justify-center gap-3">
              <BigButton onClick={() => setIndex(0)} tone="secondary">
                <ArrowLeft className="size-5" aria-hidden /> Again
              </BigButton>
              <BigButton onClick={() => navigate(home)} tone="primary">
                <Home className="size-5" aria-hidden /> Another job
              </BigButton>
            </div>
          </section>
        ) : (
          <section className="grid flex-1 content-start gap-5 py-4" aria-live="polite">
            <div className="flex items-start gap-3">
              <span
                className={cn(
                  "grid size-12 shrink-0 place-items-center rounded-full text-2xl font-extrabold text-white",
                  tone.solid,
                  tone.solid === "bg-gold-500" && "text-navy-900",
                )}
                aria-hidden
              >
                {index + 1}
              </span>
              <h1
                ref={heading}
                tabIndex={-1}
                className="flex-1 pt-1.5 text-[clamp(1.5rem,4.5vw,2rem)] font-bold leading-snug text-ink-900 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
              >
                <span className="sr-only">
                  Step {index + 1} of {task.steps.length}:{" "}
                </span>
                {say(step.words, LANG)}
              </h1>
              <SpeakButton text={say(step.words, LANG)} />
            </div>
            <div className="py-4">
              <Picture step={step} />
            </div>
          </section>
        )}

        {!done && (
          <nav
            className="sticky bottom-0 -mx-4 mt-auto flex gap-3 bg-canvas/95 px-4 pb-4 pt-3 backdrop-blur sm:-mx-6 sm:px-6"
            aria-label="Steps"
          >
            <BigButton onClick={back} tone="secondary" disabled={index === 0} label="Back">
              <ArrowLeft className="size-6" aria-hidden />
            </BigButton>
            <BigButton
              onClick={next}
              tone={index === task.steps.length - 1 ? "money" : "primary"}
              grow
            >
              {index === task.steps.length - 1 ? (
                <>
                  <Check className="size-6" aria-hidden /> Done
                </>
              ) : (
                <>
                  Next <ArrowRight className="size-6" aria-hidden />
                </>
              )}
            </BigButton>
          </nav>
        )}
      </main>

      {/* ── On paper: the whole job as a poster ─────────────────────────── */}
      <section className="hidden print:block">
        <div className="mb-4 flex items-center justify-between border-b-4 border-navy-900 pb-3">
          <p className="text-3xl font-extrabold text-ink-900">{say(task.title, LANG)}</p>
          <Logo height={40} label="Jalusi SmartSpaza" />
        </div>
        <ol className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-4">
          {task.steps.map((s, i) => (
            <li key={i} className="grid content-start gap-2 break-inside-avoid">
              <p className="flex items-start gap-2 text-lg font-bold text-ink-900">
                <span className="grid size-8 shrink-0 place-items-center rounded-full bg-navy-900 text-white">
                  {i + 1}
                </span>
                {say(s.words, LANG)}
              </p>
              <Picture step={s} />
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function Progress({ total, at, solid }: { total: number; at: number; solid: string }) {
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={Math.min(at + 1, total)}
      aria-label="Steps done"
      className="flex gap-1.5"
    >
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={cn("h-2.5 flex-1 rounded-full transition-colors", i <= at ? solid : "bg-line")}
        />
      ))}
    </div>
  );
}

const BIG_TONES = {
  primary: "bg-brand-500 text-white hover:bg-brand-600",
  money: "bg-money-500 text-white hover:bg-money-600",
  secondary: "border-2 border-line bg-surface text-ink-900 hover:bg-canvas",
};

function BigButton({
  onClick,
  tone,
  disabled,
  grow,
  label,
  children,
}: {
  onClick: () => void;
  tone: keyof typeof BIG_TONES;
  disabled?: boolean;
  grow?: boolean;
  label?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        "inline-flex h-16 min-w-16 items-center justify-center gap-2 rounded-2xl px-6 text-xl font-bold shadow-card focus:outline-none focus-visible:ring-4 focus-visible:ring-gold-500 disabled:opacity-30",
        BIG_TONES[tone],
        grow && "flex-1",
      )}
    >
      {children}
    </button>
  );
}

// ── Reading aloud ──────────────────────────────────────────────────────────

function canSpeak(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

function stopSpeaking() {
  if (canSpeak()) window.speechSynthesis.cancel();
}

/** Reads the step out, in the device's own voice. Offline on most devices. */
function SpeakButton({ text }: { text: string }) {
  if (!canSpeak()) return null;
  return (
    <button
      type="button"
      onClick={() => {
        stopSpeaking();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = "en-ZA";
        utterance.rate = 0.9;
        window.speechSynthesis.speak(utterance);
      }}
      aria-label="Read it to me"
      className="grid size-14 shrink-0 place-items-center rounded-full bg-brand-50 text-brand-500 hover:bg-brand-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-gold-500"
    >
      <Volume2 className="size-7" aria-hidden />
    </button>
  );
}
